import React, { useMemo, useContext } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { dollarFormat } from 'utils/utils';
import { LoaderComponent } from 'shared/LoaderComponent';
import { useListAccountsQuery } from 'hooks/queries/useListAccountsQuery';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { UserDetailsContext } from 'context/userDetailsContext';
import { Button } from 'primereact/button';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';

/**
 * TemporaryFundsFiltersV2 Component
 * Filter component for Temporary Funds V2 with date range and account selection
 *
 * @param {Object} props
 * @param {Object} props.filter - Filter state object
 * @param {Function} props.filterOnChange - Handler for filter changes
 * @param {Function} props.refreshData - Handler for resetting filters
 * @param {boolean} props.caseView - Whether in case view mode
 * @param {number} props.temporaryFundBalance - Current temporary fund balance
 */
const TemporaryFundsFiltersV2 = ({ filter, filterOnChange, refreshData, caseView, temporaryFundBalance }) => {
  const userContext = useContext(UserDetailsContext);

  // Dynamic payload for filtering trust accounts
  const payloadData = useMemo(
    () => ({
      account_type: 'trust',
    }),
    []
  );

  // Fetch accounts using React Query with dynamic payload
  // Note: This query will automatically refetch when accounts are created/updated elsewhere
  // (e.g., in CreateAccount.js) because those mutations invalidate queryKeys.accounts.all
  // which matches all queries starting with ['accounts']
  const { accountList, isAccountLoading: isAccountsLoading } = useListAccountsQuery({
    payloadData,
    excludeFromLoading: true,
  });

  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-2 w-100 align-items-center justify-content-between">
      <div className="ms-2 ">
        {(filter?.account_id || caseView) && (
          <>
            Balance: <span className="text-bold black-600 ">{dollarFormat(temporaryFundBalance)}</span>
          </>
        )}
      </div>
      <div className="d-block d-lg-none w-100">
        <Button
          className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          aria-controls="temporary-filter-section"
          label="Filters"
        >
          <span
            style={{
              fontSize: 18,
              transition: 'transform 0.2s',
              display: 'inline-block',
              transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <i className="fa fa-solid fa-angle-down"></i>
          </span>
        </Button>
      </div>
      <div
        className={`d-flex flex-wrap align-items-center gap-2 filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
        id="temporary-filter-section"
        aria-hidden={!showFilters}
      >
        <label className="text-bold black-600 p-0 m-0 filter-label">Filter:</label>
        <div className="d-flex flex-column filter-wraper">
          <TimezoneDatePicker
            className="input-shadow filter-calender"
            id="icon"
            name="dateFilter"
            selectionMode="range"
            value={filter?.dateFilter}
            onChange={(value) => filterOnChange({ target: { name: 'dateFilter', value } })}
            showIcon
            placeholder="Date"
            timezone={userContext?.userDetails?.timezone}
            isEndOfDay={true}
          />
        </div>
        {!caseView && (
          <div className="d-flex flex-column filter-wraper">
            <Dropdown
              placeholder="By Account"
              name="account_id"
              options={accountList || []}
              value={filter?.account_id || null}
              onChange={filterOnChange}
              optionLabel={'account_name'}
              optionValue="account_id"
              className="w-100"
              filter
              showClear
              panelFooterTemplate={isAccountsLoading ? LoaderComponent : null}
            />
          </div>
        )}
        <div className="d-flex flex-column mx-2">
          <div className="pointer ms-0">
            <i className="fas fa-sync-alt" onClick={refreshData}></i>
            <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemporaryFundsFiltersV2;
